import React, { useMemo, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Button as BsButton } from 'react-bootstrap';
import {
  Card,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { GoFileZip } from 'react-icons/go';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import FileTypeChip from 'DataSources/file/FileTypeChip';
import { isArchivePath, ARCHIVE_BOUNDARY } from 'Utils/archivePath';
import AppContext from 'Utils/AppContext';
import StartDiscoverDialog from './StartDiscoverDialog';

const CATEGORY_TO_TYPE = {
  DELIMITED:       'FILE_DELIMITED_FOLDER',
  COLUMNAR:        'FILE_PARQUET_FOLDER',
  EXCEL:           'FILE_EXCEL_FOLDER',
  STRUCTURED_JSON: 'FILE_JSON_FOLDER',
  STRUCTURED_XML:  'FILE_XML_FOLDER',
  FIXED_WIDTH:     'FILE_FIXED_WIDTH_FOLDER',
  HL7_MESSAGE:     'FILE_HL7_FOLDER',
  EDI_INTERCHANGE: 'FILE_EDI_FOLDER',
  // Since 2026-07-15 the worker surfaces document folders below the sampling depth as
  // candidate rows too (datasource_type is set on the wire; this is the fallback).
  DOCUMENT:        'FILE_DOCUMENT_FOLDER',
};

const RUNNING_STATES = new Set(['RUNNING', 'QUEUED']);

function trimTrailingSlash(p) {
  return (p || '').replace(/\/+$/, '');
}

function lastPathSegment(p) {
  const trimmed = trimTrailingSlash(p);
  const i = trimmed.lastIndexOf('/');
  return i >= 0 ? trimmed.slice(i + 1) : trimmed;
}

function displayFolderPath(p) {
  const trimmed = trimTrailingSlash(p);
  const boundary = trimmed.indexOf(ARCHIVE_BOUNDARY);
  if (boundary >= 0) {
    const beforeBoundary = trimmed.slice(0, boundary);
    const lastSlash = beforeBoundary.lastIndexOf('/');
    const archiveName = lastSlash >= 0 ? beforeBoundary.slice(lastSlash + 1) : beforeBoundary;
    return archiveName + trimmed.slice(boundary);
  }
  const segments = trimmed.split('/').filter(Boolean);
  if (segments.length === 0) return p;
  if (segments.length === 1) return segments[0];
  return segments.slice(-2).join('/');
}

const DEFAULT_TITLE = 'Other data sources detected';
// One-off files mixed into a folder are sampled in place by Folder Search since
// 2026-07-15 — only dedicated folders (several same-type files) and folders below
// the sampling depth land here, so the copy must not imply these files were the
// only structured content found.
const DEFAULT_DESCRIPTION =
  'Dedicated data folders inside this scan (several same-type files, or folders below '
  + 'the sampling depth) — run a separate discovery to cover their contents. One-off '
  + 'files were already sampled in this run.';

const CandidateSourcesPanel = ({
  candidateSources,
  connections,
  vfsScheme = 'LOCAL',
  parentSourceName = '',
  parentSourceId = null,
  discoveryBySourceId = {},
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  collapsible = false,
  defaultCollapsed = false,
  collapsedTitle,
  expandedTitle,
  maxRows,
  // Full-page mode (candidate sources page): adds a search box and clickable
  // sortable column headers. The inline overview embed keeps the plain table.
  searchable = false,
  sortable = false,
  // "Discover everything" fan-out ledger rows for this run (GET /fanout), keyed
  // against candidates on folder_path|datasource_type. Empty/absent when the run
  // did not opt in — the panel then behaves exactly as before.
  fanoutRows = [],
  onCancelFanout = null,
}) => {
  const history = useHistory();
  const { searchStatuses, locallyStartedSourceIds } = useContext(AppContext);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [dialogExisting, setDialogExisting] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [sortBy, setSortBy] = useState('folder');   // 'folder' | 'type' | 'files'
  const [sortDir, setSortDir] = useState('asc');
  const [typeFilter, setTypeFilter] = useState(null); // null = all, else Set of DatasourceType
  const [discoveredFilter, setDiscoveredFilter] = useState('all'); // all | discovered | not-discovered

  const coverageIndex = useMemo(() => {
    const idx = new Map();
    (connections || []).forEach((c) => {
      const rootPath = c?.config?.rootPath || c?.rootPath;
      const t = c?.datasourceType || c?.fileType;
      if (rootPath && t) {
        idx.set(`${trimTrailingSlash(rootPath)}|${t}`, c);
      }
    });
    return idx;
  }, [connections]);

  const runningBySourceId = useMemo(() => {
    const map = new Set();
    (searchStatuses || []).forEach((s) => {
      if (s && s.dataSourceId != null && RUNNING_STATES.has(s.state || s.glue_status)) {
        map.add(Number(s.dataSourceId));
      }
    });
    return map;
  }, [searchStatuses]);

  if (!candidateSources || candidateSources.length === 0) {
    return null;
  }

  const openDialog = (row, existing) => {
    setDialogRow(row);
    setDialogExisting(existing || null);
    setDialogOpen(true);
  };

  const handleSort = (key) => {
    if (!sortable) return;
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'files' ? 'desc' : 'asc');
    }
  };

  const rowType = (row) => row.datasource_type || CATEGORY_TO_TYPE[row.classification];

  // Fan-out ledger keyed the same way as coverage. A ledger row's
  // child_data_source_id also serves as the sourceId fallback so Running/completed
  // resolve before the global connections list has refreshed with the new children.
  const fanoutByKey = new Map();
  (fanoutRows || []).forEach((f) => {
    if (f && f.folder_path && f.datasource_type) {
      fanoutByKey.set(`${trimTrailingSlash(f.folder_path)}|${f.datasource_type}`, f);
    }
  });

  // Coverage/action state resolved up front so the discovered filter can key off it;
  // the render below consumes these instead of recomputing per cell.
  const derived = candidateSources.map((row) => {
    const dsType = rowType(row);
    const key = `${trimTrailingSlash(row.folder_path)}|${dsType}`;
    const existing = coverageIndex.get(key);
    const fanout = fanoutByKey.get(key) || null;
    const sourceId = existing
      ? Number(existing.id)
      : (fanout && fanout.child_data_source_id != null ? Number(fanout.child_data_source_id) : null);
    const isLocallyStarted = sourceId != null && (locallyStartedSourceIds || []).indexOf(sourceId) >= 0;
    const isRunning = isLocallyStarted || (sourceId != null && runningBySourceId.has(sourceId));
    const hasCompleted = !isLocallyStarted && sourceId != null && discoveryBySourceId[sourceId] != null;
    return { row, dsType, existing, fanout, sourceId, isRunning, hasCompleted };
  });

  const fanoutCancellable = derived.some(({ fanout, isRunning }) =>
    fanout && (fanout.state === 'PENDING' || fanout.state === 'REUSED_SOURCE'
      || (fanout.state === 'STARTED' && isRunning)));

  const presentTypes = Array.from(new Set(derived.map((d) => d.dsType).filter(Boolean)));

  let rows = derived;
  const term = searchable ? searchField.trim().toLowerCase() : '';
  if (term) {
    rows = rows.filter(({ row, dsType }) =>
      String(row.folder_path || '').toLowerCase().includes(term)
      || String(dsType || '').toLowerCase().includes(term)
      || String(row.classification || '').toLowerCase().includes(term));
  }
  if (typeFilter) {
    rows = rows.filter((d) => typeFilter.has(d.dsType));
  }
  // "Discovered" = a matching source exists and a discovery has completed or is
  // currently running; "not discovered" = the candidate still needs action.
  if (discoveredFilter === 'discovered') {
    rows = rows.filter((d) => d.hasCompleted || d.isRunning);
  } else if (discoveredFilter === 'not-discovered') {
    rows = rows.filter((d) => !(d.hasCompleted || d.isRunning));
  }
  if (sortable) {
    rows = rows.slice().sort((a, b) => {
      let cmp;
      if (sortBy === 'files') {
        cmp = (Number(a.row.file_count) || 0) - (Number(b.row.file_count) || 0);
      } else if (sortBy === 'type') {
        cmp = String(a.dsType || '').localeCompare(String(b.dsType || ''));
      } else {
        cmp = String(a.row.folder_path || '').localeCompare(String(b.row.folder_path || ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const toggleTypeFilter = (t) => {
    setTypeFilter((prev) => {
      const next = new Set(prev || []);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next.size === 0 ? null : next;
    });
  };

  const sortArrow = (key) => (sortable && sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');
  const headerCellStyle = { fontWeight: 600, cursor: sortable ? 'pointer' : 'default' };

  const headerLabel = collapsible
    ? (collapsed ? (collapsedTitle || title) : (expandedTitle || title))
    : title;
  const tableMaxHeight = maxRows ? 40 + maxRows * 37 : undefined;

  return (
    <>
      <Card style={{ padding: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: collapsible && collapsed ? 0 : 12,
            cursor: collapsible ? 'pointer' : 'default',
          }}
          onClick={collapsible ? () => setCollapsed((v) => !v) : undefined}
        >
          <Typography variant='h6' style={{ fontFamily: 'Rubik' }}>
            {headerLabel}
          </Typography>
          {fanoutCancellable && typeof onCancelFanout === 'function' && (
            <BsButton
              variant='outline-danger'
              size='sm'
              data-testid='cancel-fanout'
              onClick={(e) => { e.stopPropagation(); onCancelFanout(); }}
            >
              Cancel remaining sub-discoveries
            </BsButton>
          )}
          {collapsible && (
            <IconButton size='small' aria-label={collapsed ? 'Expand' : 'Collapse'}>
              {collapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          )}
        </div>
        <Collapse in={!collapsible || !collapsed} timeout='auto' unmountOnExit>
          <Typography
            variant='body2'
            color='textSecondary'
            style={{ fontFamily: 'Rubik', marginBottom: 12 }}
          >
            {description}
          </Typography>
          {searchable && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                placeholder='Search folders or types…'
                aria-label='Search candidate sources'
                style={{
                  minWidth: 260, borderRadius: 8, border: '1px solid #E5E8ED', background: '#fff',
                  padding: '6px 12px', fontSize: 14, outline: 'none', fontFamily: 'Rubik',
                }}
              />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Typography variant='caption' color='textSecondary' style={{ fontFamily: 'Rubik' }}>
                  Type:
                </Typography>
                {presentTypes.map((t) => (
                  <span
                    key={t}
                    onClick={() => toggleTypeFilter(t)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 10,
                      padding: 2,
                      border: typeFilter && typeFilter.has(t) ? '2px solid #315DFF' : '2px solid transparent',
                      opacity: !typeFilter || typeFilter.has(t) ? 1 : 0.45,
                      display: 'inline-flex',
                    }}
                    title={typeFilter && typeFilter.has(t) ? 'Click to remove from filter' : 'Click to filter by this type'}
                  >
                    <FileTypeChip type={t} />
                  </span>
                ))}
              </span>
              <select
                value={discoveredFilter}
                onChange={(e) => setDiscoveredFilter(e.target.value)}
                aria-label='Filter by discovery state'
                style={{
                  borderRadius: 8, border: '1px solid #E5E8ED', background: '#fff',
                  padding: '6px 10px', fontSize: 13, fontFamily: 'Rubik', outline: 'none',
                }}
              >
                <option value='all'>All discovery states</option>
                <option value='discovered'>Discovered (done or running)</option>
                <option value='not-discovered'>Not discovered</option>
              </select>
              {(typeFilter || discoveredFilter !== 'all') && (
                <button
                  type='button'
                  onClick={() => { setTypeFilter(null); setDiscoveredFilter('all'); }}
                  style={{ border: 'none', background: 'none', color: '#315DFF', fontSize: 12, cursor: 'pointer' }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
          <TableContainer style={{ maxHeight: tableMaxHeight, overflowY: tableMaxHeight ? 'auto' : undefined }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle} onClick={() => handleSort('folder')}>
                    Folder{sortArrow('folder')}
                  </TableCell>
                  <TableCell style={headerCellStyle} onClick={() => handleSort('type')}>
                    Type{sortArrow('type')}
                  </TableCell>
                  <TableCell align='right' style={headerCellStyle} onClick={() => handleSort('files')}>
                    Files{sortArrow('files')}
                  </TableCell>
                  <TableCell align='right' style={{ fontWeight: 600 }}>Discovery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} style={{ color: '#8A92A0', fontFamily: 'Rubik' }}>
                      No candidate sources match the current
                      {term ? ` search (“${searchField.trim()}”)` : ''} filters.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map(({ row, dsType, existing, fanout, sourceId, isRunning, hasCompleted }) => {
                  let discoveryCell;
                  if (fanout && fanout.state === 'FAILED') {
                    // Auto-create/start failed — keep the manual Discover path available.
                    discoveryCell = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Tooltip title={fanout.error || 'Automatic sub-discovery failed'} placement='top'>
                          <Typography
                            variant='caption'
                            style={{ fontFamily: 'Rubik', color: '#B91C1C' }}
                          >
                            Auto-create failed
                          </Typography>
                        </Tooltip>
                        <BsButton
                          variant='outline-primary'
                          size='sm'
                          onClick={() => openDialog(row, existing)}
                        >
                          Discover
                        </BsButton>
                      </span>
                    );
                  } else if (fanout && fanout.state === 'CANCELLED') {
                    discoveryCell = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Typography
                          variant='caption'
                          style={{ fontFamily: 'Rubik', color: '#8A92A0' }}
                        >
                          Cancelled
                        </Typography>
                        <BsButton
                          variant='outline-primary'
                          size='sm'
                          onClick={() => openDialog(row, existing)}
                        >
                          Discover
                        </BsButton>
                      </span>
                    );
                  } else if (fanout
                      && (fanout.state === 'PENDING' || fanout.state === 'REUSED_SOURCE')
                      && !isRunning && !hasCompleted) {
                    // Planned, waiting for a dispatch slot (children start a few at a time).
                    discoveryCell = (
                      <Typography
                        variant='caption'
                        style={{ fontFamily: 'Rubik', color: '#8A92A0', fontStyle: 'italic' }}
                      >
                        Queued…
                      </Typography>
                    );
                  } else if (!existing && !fanout) {
                    discoveryCell = (
                      <BsButton
                        variant='outline-primary'
                        size='sm'
                        onClick={() => openDialog(row, null)}
                      >
                        Discover
                      </BsButton>
                    );
                  } else if (isRunning) {
                    discoveryCell = (
                      <Typography
                        variant='caption'
                        style={{ fontFamily: 'Rubik', color: '#8A92A0', fontStyle: 'italic' }}
                      >
                        Running…
                      </Typography>
                    );
                  } else if (hasCompleted) {
                    discoveryCell = (
                      <BsButton
                        variant='link'
                        size='sm'
                        style={{ padding: 0 }}
                        onClick={() =>
                          history.push(`/landscape/riskprofile/${encodeURIComponent(sourceId)}`)
                        }
                      >
                        View risk profile →
                      </BsButton>
                    );
                  } else {
                    discoveryCell = (
                      <BsButton
                        variant='outline-primary'
                        size='sm'
                        onClick={() => openDialog(row, existing)}
                      >
                        Discover
                      </BsButton>
                    );
                  }

                  return (
                    <TableRow key={`${row.folder_path}-${dsType}`} hover>
                      <TableCell>
                        <Tooltip
                          title={
                            isArchivePath(row.folder_path)
                              ? `${row.folder_path} (inside archive)`
                              : row.folder_path
                          }
                          placement='top'
                        >
                          <span
                            style={{
                              fontFamily: 'Rubik',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {isArchivePath(row.folder_path) && (
                              <GoFileZip
                                aria-label='In archive'
                                style={{ flexShrink: 0, opacity: 0.7 }}
                              />
                            )}
                            {displayFolderPath(row.folder_path)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <FileTypeChip type={dsType} />
                          {row.delimiter && (
                            <Typography
                              variant='caption'
                              color='textSecondary'
                              style={{ fontFamily: 'monospace' }}
                            >
                              {JSON.stringify(row.delimiter)}
                            </Typography>
                          )}
                        </span>
                      </TableCell>
                      <TableCell align='right' style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.file_count != null ? Number(row.file_count).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell align='right'>
                        {discoveryCell}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Card>
      <StartDiscoverDialog
        show={dialogOpen}
        onHide={() => setDialogOpen(false)}
        candidateRow={dialogRow}
        existingSource={dialogExisting}
        parentSourceName={parentSourceName}
        parentSourceId={parentSourceId}
        vfsScheme={vfsScheme}
      />
    </>
  );
};

export default CandidateSourcesPanel;
